import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import {
  Box,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import { Edit as EditIcon, Block as BlockIcon } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import ManagementPageLayout from '../../components/layout/ManagementPageLayout';
import FolderTabs from '../../components/common/FolderTabs';
import ManagementPageSkeleton from '../../components/layout/ManagementPageSkeleton';
import PrimaryActionButton from '../../components/common/PrimaryActionButton';
import ConfirmDialog from '../../components/common/ConfirmDialog';
import SalovareDateRangePicker from '../../components/common/SalovareDateRangePicker';
import ExpenseEditorDialog from '../../components/expenses/ExpenseEditorDialog';
import RecurrenceScopeDialog from '../../components/expenses/RecurrenceScopeDialog';
import MoneyMovementsPanel from '../../components/expenses/MoneyMovementsPanel';
import RevenueReportPanel from '../../components/expenses/RevenueReportPanel';
import SalesListPanel from '../../components/expenses/SalesListPanel';
import { MoloniConnectionPanel } from '../../components/moloni/IssueInvoiceDialog';
import { useToast } from '../../context/ToastContext';
import { useAppData } from '../../context/AppDataContext';
import usePermissions from '../../hooks/usePermissions';
import { Permissions } from '../../constants/Permissions';
import ExpenseService from '../../services/expenseService';
import SupplierService from '../../services/supplierService';
import requestDeduplicator from '../../utils/requestDeduplicator';
import { describeApiError } from '../../utils/stockErrorUtils';
import { formatCalendarDate, parseCalendarDateKey, toCalendarDateKey } from '../../utils/dateFormatUtils';
import { formatCurrency } from '../../utils/currencyUtils';
import { BRAND } from '../../theme/brandTokens';
import { isPortugueseStore } from '../../utils/portugueseStore';

const monthRange = () => {
  const now = new Date();
  const from = new Date(now.getFullYear(), now.getMonth(), 1);
  const to = new Date(now.getFullYear(), now.getMonth() + 1, 0);
  return { from: toCalendarDateKey(from), to: toCalendarDateKey(to) };
};

const ExpensesPage = () => {
  const { t } = useTranslation();
  const { storeId } = useParams();
  const { currentStore, stores } = useAppData();
  const { showSuccess, showError } = useToast();
  const { hasPermission } = usePermissions(storeId);
  const canWrite = hasPermission(Permissions.ExpensesWrite);
  const [searchParams, setSearchParams] = useSearchParams();
  const tab = searchParams.get('tab') || 'expenses';
  const [range, setRange] = useState(monthRange);
  const [loading, setLoading] = useState(true);
  const [expenses, setExpenses] = useState([]);
  const [summary, setSummary] = useState(null);
  const [suppliers, setSuppliers] = useState([]);
  const [editor, setEditor] = useState({ open: false, expense: null, scope: null });
  const [scopeAction, setScopeAction] = useState(null);
  const [voidTarget, setVoidTarget] = useState(null);
  const [saving, setSaving] = useState(false);
  const [moloniReady, setMoloniReady] = useState(false);
  const currency = currentStore?.currency || 'EUR';

  const load = useCallback(() => {
    if (!storeId) return undefined;
    let cancelled = false;
    setLoading(true);
    requestDeduplicator.deduplicate(
      `expenses-${storeId}-${range.from}-${range.to}-${tab}`,
      () => Promise.all([
        ExpenseService.getExpenses(storeId, range),
        ExpenseService.getSummary(storeId, range),
        SupplierService.getSuppliers(storeId),
      ]),
    ).then(([expensePage, expenseSummary, supplierList]) => {
      if (cancelled) return;
      setExpenses(expensePage.items);
      setSummary(expenseSummary);
      setSuppliers(supplierList);
    }).catch((error) => {
      console.error('Failed to load expenses:', error);
      if (!cancelled) showError(describeApiError(error, t, 'expenses.errors.load'));
    }).finally(() => {
      if (!cancelled) setLoading(false);
    });
    return () => {
      cancelled = true;
    };
  }, [range, showError, storeId, t, tab]);

  useEffect(() => load(), [load]);

  const seriesRows = useMemo(() => {
    const grouped = new Map();
    expenses.filter((item) => item.seriesId).forEach((item) => {
      const current = grouped.get(item.seriesId);
      if (!current || (item.seriesSequence || 0) < (current.seriesSequence || 0)) {
        grouped.set(item.seriesId, item);
      }
    });
    return [...grouped.values()];
  }, [expenses]);

  const handleSave = async (expense) => {
    try {
      setSaving(true);
      if (editor.expense?.id) {
        await ExpenseService.updateExpense(storeId, editor.expense.id, expense, editor.scope);
      } else {
        await ExpenseService.createExpense(storeId, expense);
      }
      showSuccess(t('expenses.feedback.saved'));
      setEditor({ open: false, expense: null, scope: null });
      load();
    } catch (error) {
      console.error('Failed to save expense:', error);
      showError(describeApiError(error, t, 'expenses.errors.save'));
    } finally {
      setSaving(false);
    }
  };

  const beginEdit = (expense) => {
    if (expense.sourceType === 'StockPurchase') {
      showError(t('expenses.errors.readOnly'));
      return;
    }
    if (expense.seriesId) {
      setScopeAction({ mode: 'edit', expense });
      return;
    }
    setEditor({ open: true, expense, scope: null });
  };

  const beginVoid = (expense) => {
    if (expense.sourceType === 'StockPurchase') {
      showError(t('expenses.errors.readOnly'));
      return;
    }
    if (expense.seriesId) {
      setScopeAction({ mode: 'delete', expense });
      return;
    }
    setVoidTarget({ expense, scope: null });
  };

  const handleScopeConfirm = (scope) => {
    const action = scopeAction;
    setScopeAction(null);
    if (!action) return;
    if (action.mode === 'edit') {
      setEditor({ open: true, expense: action.expense, scope });
      return;
    }
    setVoidTarget({ expense: action.expense, scope });
  };

  const handleVoid = async () => {
    if (!voidTarget) return;
    try {
      setSaving(true);
      await ExpenseService.voidExpense(storeId, voidTarget.expense.id, { scope: voidTarget.scope });
      showSuccess(t('expenses.feedback.voided'));
      setVoidTarget(null);
      load();
    } catch (error) {
      console.error('Failed to void expense:', error);
      showError(describeApiError(error, t, 'expenses.errors.void'));
    } finally {
      setSaving(false);
    }
  };

  const renderRows = (rows) => (
    <Box sx={{ overflowX: 'auto', border: `1px solid ${BRAND.line}`, borderRadius: 2 }}>
      <Table size="small">
        <TableHead>
          <TableRow sx={{ backgroundColor: BRAND.background }}>
            <TableCell>{t('expenses.fields.date')}</TableCell>
            <TableCell>{t('expenses.fields.description')}</TableCell>
            <TableCell>{t('expenses.fields.category')}</TableCell>
            <TableCell align="right">{t('expenses.fields.amount')}</TableCell>
            <TableCell>{t('expenses.fields.status')}</TableCell>
            {canWrite ? <TableCell align="right" /> : null}
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.length === 0 ? (
            <TableRow>
              <TableCell colSpan={canWrite ? 6 : 5}>
                <Typography variant="body2" color="text.secondary">{t('expenses.empty')}</Typography>
              </TableCell>
            </TableRow>
          ) : rows.map((item) => (
            <TableRow key={item.id}>
              <TableCell>{formatCalendarDate(item.expenseDate)}</TableCell>
              <TableCell>{item.description}</TableCell>
              <TableCell>{t(`expenses.categories.${item.category}`)}</TableCell>
              <TableCell align="right">{formatCurrency(item.amount, currency)}</TableCell>
              <TableCell>
                {item.isScheduled ? t('expenses.status.scheduled') : t(`expenses.status.${item.status}`)}
              </TableCell>
              {canWrite ? (
                <TableCell align="right">
                  <Tooltip title={t('expenses.actions.edit')}>
                    <IconButton aria-label={t('expenses.actions.edit')} onClick={() => beginEdit(item)} sx={{ color: BRAND.accentText }}>
                      <EditIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title={t('expenses.actions.void')}>
                    <IconButton aria-label={t('expenses.actions.void')} onClick={() => beginVoid(item)} sx={{ color: 'error.main' }}>
                      <BlockIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </TableCell>
              ) : null}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Box>
  );

  const pageStore = stores.find((store) => store.id === storeId) || currentStore;
  const showInvoicing = isPortugueseStore(pageStore?.country);
  const tabs = [
    { value: 'expenses', label: t('expenses.tabs.expenses') },
    { value: 'recurring', label: t('expenses.tabs.recurring') },
    { value: 'money', label: t('expenses.tabs.money') },
    { value: 'revenue', label: t('expenses.tabs.revenue') },
    { value: 'sales', label: t('expenses.tabs.sales') },
    ...(showInvoicing ? [{ value: 'invoicing', label: t('expenses.tabs.invoicing') }] : []),
  ];
  const activeTab = showInvoicing || tab !== 'invoicing' ? tab : 'expenses';

  if (loading) {
    return (
      <ManagementPageSkeleton pageTitle={t('expenses.title')} pageSubtitle={t('expenses.subtitle')} variant="cards" />
    );
  }

  return (
    <ManagementPageLayout id="expenses-page" pageTitle={t('expenses.title')} pageSubtitle={t('expenses.subtitle')}>
      <FolderTabs
        tabs={tabs}
        value={activeTab}
        onChange={(next) => setSearchParams(next === 'expenses' ? {} : { tab: next })}
        contentId="expenses-tabs-content"
      >
        <Box sx={{ px: { xs: 2, sm: 3 }, pt: { xs: 2, sm: 3 } }}>
          <SalovareDateRangePicker
            startValue={parseCalendarDateKey(range.from)}
            endValue={parseCalendarDateKey(range.to)}
            onStartChange={(date) => setRange((previous) => ({
              ...previous,
              from: date ? toCalendarDateKey(date) : previous.from,
            }))}
            onEndChange={(date) => setRange((previous) => ({
              ...previous,
              to: date ? toCalendarDateKey(date) : previous.to,
            }))}
            sx={{
              width: '100%',
              maxWidth: { xs: '100%', sm: 400 },
            }}
          />
        </Box>
        <Box role="tabpanel" hidden={activeTab !== 'expenses'} id="expenses-tabpanel-list" sx={{ p: { xs: 2, sm: 3 } }}>
          {canWrite ? (
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
              <PrimaryActionButton id="create-expense" onClick={() => setEditor({ open: true, expense: null, scope: null })}>
                {t('expenses.actions.create')}
              </PrimaryActionButton>
            </Box>
          ) : null}
          {activeTab === 'expenses' ? renderRows(expenses) : null}
        </Box>
        <Box role="tabpanel" hidden={activeTab !== 'recurring'} id="expenses-tabpanel-recurring" sx={{ p: { xs: 2, sm: 3 } }}>
          {activeTab === 'recurring' ? renderRows(seriesRows) : null}
        </Box>
        <Box role="tabpanel" hidden={activeTab !== 'money'} id="expenses-tabpanel-money">
          {activeTab === 'money' ? <MoneyMovementsPanel summary={summary} currency={currency} /> : null}
        </Box>
        <Box role="tabpanel" hidden={activeTab !== 'revenue'} id="expenses-tabpanel-revenue" sx={{ p: { xs: 2, sm: 3 } }}>
          {activeTab === 'revenue' ? <RevenueReportPanel storeId={storeId} from={range.from} to={range.to} currency={currency} /> : null}
        </Box>
        <Box role="tabpanel" hidden={activeTab !== 'sales'} id="expenses-tabpanel-sales" sx={{ p: { xs: 2, sm: 3 } }}>
          {activeTab === 'sales' ? <SalesListPanel storeId={storeId} currency={currency} canWrite={canWrite} /> : null}
        </Box>
        <Box role="tabpanel" hidden={!showInvoicing || activeTab !== 'invoicing'} id="expenses-tabpanel-invoicing" sx={{ p: { xs: 2, sm: 3 } }}>
          {showInvoicing && activeTab === 'invoicing' ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
              <MoloniConnectionPanel storeId={storeId} canWrite={canWrite} onReadyChange={setMoloniReady} />
              {moloniReady ? (
                <SalesListPanel storeId={storeId} currency={currency} canWrite={canWrite} invoicing range={range} />
              ) : null}
            </Box>
          ) : null}
        </Box>
      </FolderTabs>

      <ExpenseEditorDialog
        open={editor.open}
        expense={editor.expense}
        suppliers={suppliers}
        saving={saving}
        onClose={() => setEditor({ open: false, expense: null, scope: null })}
        onSave={handleSave}
      />
      <RecurrenceScopeDialog
        open={Boolean(scopeAction)}
        mode={scopeAction?.mode}
        onClose={() => setScopeAction(null)}
        onConfirm={handleScopeConfirm}
      />
      <ConfirmDialog
        open={Boolean(voidTarget)}
        onClose={() => setVoidTarget(null)}
        onConfirm={handleVoid}
        title={t('expenses.void.title')}
        message={t('expenses.void.message')}
        confirmText={t('expenses.actions.void')}
        cancelText={t('common.cancel')}
      />
    </ManagementPageLayout>
  );
};

export default ExpensesPage;
